import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { workspaces as workspacesApi } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/Icon';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/useToast';

/**
 * Troca de empresa.
 *
 * A mesma pessoa pode trabalhar em mais de uma empresa da plataforma: um contador
 * que atende duas corretoras, um socio de dois grupos. O schema sempre admitiu isso,
 * porque identidade e vinculo sao tabelas separadas desde a fundacao.
 *
 * O botao so aparece para quem tem mais de uma. Para todo o resto ele seria um
 * controle que nao leva a lugar nenhum.
 *
 * A troca acontece no servidor: ele confere o vinculo, abre outra sessao e encerra a
 * anterior. A tela nao filtra nada por conta propria, e uma empresa que o navegador
 * inventasse simplesmente nao teria vinculo.
 */
export function WorkspaceSwitcher() {
  const { session, switchTenant } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [aberto, setAberto] = useState(false);
  const [trocando, setTrocando] = useState<string | null>(null);
  const caixa = useRef<HTMLDivElement>(null);

  const consulta = useQuery({
    queryKey: ['workspaces'],
    queryFn: ({ signal }) => workspacesApi.list(signal),
    // A lista muda quando alguem ganha ou perde vinculo, o que e raro. Uma janela
    // maior evita uma consulta a cada montagem do cabecalho.
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!aberto) return;
    function fora(evento: MouseEvent) {
      if (caixa.current && !caixa.current.contains(evento.target as Node)) {
        setAberto(false);
      }
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const empresas = consulta.data ?? [];

  // Uma empresa so nao e uma escolha.
  if (empresas.length < 2) return null;

  async function trocar(slug: string) {
    if (slug === session?.tenant.slug) {
      setAberto(false);
      return;
    }

    setTrocando(slug);
    try {
      await switchTenant(slug);
      // O que ja foi carregado pertence a empresa anterior. Manter em cache faria a
      // tela mostrar, por um instante, dados de uma empresa em que a pessoa nao
      // esta mais.
      queryClient.clear();
      setAberto(false);
    } catch (causa) {
      toast.error(
        causa instanceof Error ? causa.message : 'Nao foi possivel trocar de empresa.',
      );
    } finally {
      setTrocando(null);
    }
  }

  return (
    <div className="relative" ref={caixa}>
      <button
        type="button"
        onClick={() => setAberto((atual) => !atual)}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label="Trocar de empresa"
        className={cn(
          'flex max-w-52 items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5',
          'text-xs font-medium text-ink-700 transition-colors duration-(--duration-fast)',
          'hover:bg-ink-50',
        )}
      >
        <Icon name="briefcase" size={14} className="shrink-0 text-ink-500" />
        <span className="truncate">{session?.tenant.name}</span>
      </button>

      {aberto ? (
        <div
          role="menu"
          className="surface-card absolute right-0 z-30 mt-2 w-64 p-1.5 shadow-(--shadow-lg)"
        >
          <p className="px-2.5 py-1.5 text-2xs font-semibold text-ink-500">
            Suas empresas
          </p>
          {empresas.map((item) => {
            const atual = item.tenant.slug === session?.tenant.slug;
            return (
              <button
                key={item.tenant.id}
                type="button"
                role="menuitem"
                disabled={trocando !== null}
                onClick={() => void trocar(item.tenant.slug)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs',
                  'hover:bg-ink-100 disabled:opacity-60',
                  atual ? 'font-semibold text-ink-900' : 'text-ink-700',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{item.tenant.name}</span>
                {trocando === item.tenant.slug ? (
                  <Spinner size={13} label="Trocando de empresa" />
                ) : atual ? (
                  <Icon name="check" size={14} className="shrink-0 text-brand-500" />
                ) : null}
              </button>
            );
          })}
          <p className="border-t border-line px-2.5 pt-2 pb-1 text-2xs text-ink-500">
            Trocar abre outra sessao e encerra a atual.
          </p>
        </div>
      ) : null}
    </div>
  );
}
